import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Link,
  TableSortLabel,
  Stack,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { Search, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import BasicButton from '../../../components/button/BasicButton';
import ActionDialog from '../../../components/dialog/ActionDialog';
import { adminApi } from '../../../api/admin';
import ProgressBar from '../../../components/common/ProgressBar';
import ErrorState from '../../../components/common/ErrorStats';
import NoData from '../../../components/common/NoData';
import Pagination from '../../../components/common/Pagination';
import UserSuspensionDialog from '../../../components/dialog/UserSuspensionDialog';
import GradeChangeDialog from '../../../components/dialog/GradeChangeDialog';
import ForceWithdrawDialog from '../../../components/dialog/ForceWithdrawDialog';
import RejoinableChangeDialog from '../../../components/dialog/RejoinableChangeDialog';
import theme from '../../../theme';
import { GRADE_LABELS } from '../../../utils/formatter';

const ROWS_PER_PAGE = 20;

// 탭별 설정
const TAB_CONFIG = {
  active: {
    headCells: [
      { id: 'nickname', label: '닉네임(이메일)', sortable: false },
      { id: 'grade', label: '등급', sortable: true },
      { id: 'createdAt', label: '가입일', sortable: true },
      { id: 'lastVisitedAt', label: '최종방문일', sortable: true },
      { id: 'visitCount', label: '방문수', sortable: true },
      { id: 'reviewsCount', label: '리뷰수', sortable: true },
      { id: 'playlistsCount', label: '플리수', sortable: true },
      { id: 'commentsCount', label: '댓글수', sortable: true },
      { id: 'bookmarksCount', label: '저장수', sortable: true },
    ],
    isActive: true,
    isWithdrawn: false,
  },
  suspended: {
    headCells: [
      { id: 'nickname', label: '닉네임(이메일)', sortable: false },
      { id: 'grade', label: '등급', sortable: true },
      { id: 'createdAt', label: '가입일', sortable: true },
      { id: 'suspensionReason', label: '사유', sortable: false },
      { id: 'suspendedAt', label: '처리일', sortable: true },
    ],
    isActive: false,
    isWithdrawn: false,
  },
  withdrawn: {
    headCells: [
      { id: 'nickname', label: '닉네임(이메일)', sortable: false },
      { id: 'grade', label: '등급', sortable: true },
      { id: 'createdAt', label: '가입일', sortable: true },
      { id: 'withdrawalReason', label: '사유', sortable: false },
      { id: 'isRejoinable', label: '상태', sortable: true },
      { id: 'withdrawnAt', label: '처리일', sortable: true },
    ],
    isActive: true, // isWithdrawn=true일 때 서버에서 무시됨
    isWithdrawn: true,
  },
};

// 탭별 테이블 셀 렌더링
const renderUserCells = (user, subTab) => {
  const cellStyle = { borderRight: `1px solid ${theme.palette.grey.line}` };
  const textStyle = { fontSize: 14, color: theme.palette.black[9] };

  // 공통 셀: 닉네임, 등급, 가입일
  const commonCells = (
    <>
      <TableCell sx={cellStyle}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', whiteSpace: 'nowrap' }}>
          {subTab === 'withdrawn' ? (
            <Typography {...textStyle}>{user.nickname}</Typography>
          ) : (
            <Link
              component={RouterLink}
              to={`/users/${encodeURIComponent(user.nickname)}`}
              sx={{ textDecoration: 'none' }}
            >
              <Typography {...textStyle}>{user.nickname}</Typography>
            </Link>
          )}
          <Typography {...textStyle} marginLeft={0.5}>
            ({user.email})
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="center" sx={cellStyle}>
        <Typography {...textStyle}>{GRADE_LABELS[user.grade] || user.grade}</Typography>
      </TableCell>
      <TableCell align="right" sx={cellStyle}>
        <Typography {...textStyle}>{user.createdAt}</Typography>
      </TableCell>
    </>
  );

  if (subTab === 'active') {
    return (
      <>
        {commonCells}
        <TableCell align="right" sx={cellStyle}>
          <Typography {...textStyle}>{user.lastVisitedAt}</Typography>
        </TableCell>
        <TableCell align="right" sx={cellStyle}>
          <Typography {...textStyle}>{user.visitCount}</Typography>
        </TableCell>
        <TableCell align="right" sx={cellStyle}>
          <Typography {...textStyle}>{user.reviewsCount}</Typography>
        </TableCell>
        <TableCell align="right" sx={cellStyle}>
          <Typography {...textStyle}>{user.playlistsCount}</Typography>
        </TableCell>
        <TableCell align="right" sx={cellStyle}>
          <Typography {...textStyle}>{user.commentsCount}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography {...textStyle}>{user.bookmarksCount}</Typography>
        </TableCell>
      </>
    );
  }

  if (subTab === 'suspended') {
    return (
      <>
        {commonCells}
        <TableCell sx={{ ...cellStyle, minWidth: 535 }}>
          <Typography {...textStyle}>{user.suspensionReason || '-'}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography {...textStyle}>{user.suspendedAt || '-'}</Typography>
        </TableCell>
      </>
    );
  }

  // withdrawn
  return (
    <>
      {commonCells}
      <TableCell sx={{ ...cellStyle, minWidth: 428 }}>
        <Typography {...textStyle}>{user.withdrawalReason || '-'}</Typography>
      </TableCell>
      <TableCell align="center" sx={cellStyle}>
        <Typography {...textStyle}>{user.isRejoinable ? '가입가능' : '가입불가'}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography {...textStyle}>{user.withdrawnAt || '-'}</Typography>
      </TableCell>
    </>
  );
};

const UserManagement = ({ subTab, searchParams, updateURL, onCountsChange }) => {
  const config = TAB_CONFIG[subTab];
  const { headCells, isActive, isWithdrawn } = config;

  const debouncedSearchTerm = searchParams.get('search') || '';
  const sortField = searchParams.get('sortField') || 'createdAt';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const currentPage = parseInt(searchParams.get('page') || '1');

  const [searchTerm, setSearchTerm] = useState(debouncedSearchTerm);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({ totalPages: 1, totalItems: 0 });
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // 다이얼로그 상태
  const [isSuspensionDialogOpen, setIsSuspensionDialogOpen] = useState(false);
  const [isReactivationDialogOpen, setIsReactivationDialogOpen] = useState(false);
  const [isGradeChangeDialogOpen, setIsGradeChangeDialogOpen] = useState(false);
  const [isForceWithdrawDialogOpen, setIsForceWithdrawDialogOpen] = useState(false);
  const [isRejoinableChangeDialogOpen, setIsRejoinableChangeDialogOpen] = useState(false);

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, [subTab]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApi.getUsers(
          currentPage,
          ROWS_PER_PAGE,
          debouncedSearchTerm,
          isActive,
          isWithdrawn,
          sortField,
          sortOrder,
        );
        setUsers(response.data);
        setPaginationInfo(response.meta.pagination);
      } catch (err) {
        setError(err);
        setUsers([]);
        setPaginationInfo({ totalPages: 1, totalItems: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [
    subTab,
    currentPage,
    debouncedSearchTerm,
    refetchTrigger,
    sortField,
    sortOrder,
    isActive,
    isWithdrawn,
  ]);

  const handleSearchClick = () => {
    updateURL({ search: searchTerm || null, page: null });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleSort = (field) => {
    const isCurrentField = sortField === field;
    const newSortOrder = isCurrentField && sortOrder === 'desc' ? 'asc' : 'desc';
    updateURL({ sortField: field, sortOrder: newSortOrder, page: null });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const currentPageUserIds = users.map((user) => user.id);
      setSelectedUserIds((prev) => [...new Set([...prev, ...currentPageUserIds])]);
      return;
    }
    const currentPageUserIds = users.map((user) => user.id);
    setSelectedUserIds((prev) => prev.filter((id) => !currentPageUserIds.includes(id)));
  };

  const handleSelectClick = (event, id) => {
    const selectedIndex = selectedUserIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedUserIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedUserIds.slice(1));
    } else if (selectedIndex === selectedUserIds.length - 1) {
      newSelected = newSelected.concat(selectedUserIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedUserIds.slice(0, selectedIndex),
        selectedUserIds.slice(selectedIndex + 1),
      );
    }
    setSelectedUserIds(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    updateURL({ page: newPage });
  };

  // 액션 핸들러
  const refreshAfterAction = () => {
    setSelectedUserIds([]);
    setRefetchTrigger((c) => c + 1);
    onCountsChange?.();
  };

  const handleSuspend = async (reason) => {
    try {
      await adminApi.updateUsersStatus({ userIds: selectedUserIds, isActive: false, reason });
      setIsSuspensionDialogOpen(false);
      refreshAfterAction();
    } catch (err) {}
  };

  const handleReactivate = async () => {
    try {
      await adminApi.updateUsersStatus({ userIds: selectedUserIds, isActive: true });
      setIsReactivationDialogOpen(false);
      refreshAfterAction();
    } catch (err) {}
  };

  const handleGradeChange = async (grade) => {
    try {
      await adminApi.updateUsersGrade(selectedUserIds, grade);
      setIsGradeChangeDialogOpen(false);
      refreshAfterAction();
    } catch (err) {}
  };

  const handleForceWithdraw = async (reason) => {
    try {
      await adminApi.withdrawUsers(selectedUserIds, reason);
      setIsForceWithdrawDialogOpen(false);
      refreshAfterAction();
    } catch (err) {}
  };

  const handleRejoinableChange = async (isRejoinable) => {
    try {
      await adminApi.updateUsersRejoinable(selectedUserIds, isRejoinable);
      setIsRejoinableChangeDialogOpen(false);
      refreshAfterAction();
    } catch (err) {}
  };

  const numSelectedOnPage = useMemo(
    () => users.filter((user) => selectedUserIds.includes(user.id)).length,
    [users, selectedUserIds],
  );

  const isAllOnPageSelected = users.length > 0 && numSelectedOnPage === users.length;
  const isIndeterminate = numSelectedOnPage > 0 && !isAllOnPageSelected;
  const hasSelection = selectedUserIds.length > 0;

  // 탭별 액션 버튼 렌더링
  const renderActionButtons = () => {
    const buttonStyle = { typography: 'body3', fontWeight: 'medium', padding: '12px 16px' };

    if (subTab === 'active') {
      return (
        <Stack direction="row" spacing={1} sx={{ mb: { xs: 2.5, md: 2 } }}>
          <BasicButton
            variant="outlined"
            onClick={() => setIsGradeChangeDialogOpen(true)}
            disabled={!hasSelection}
            sx={buttonStyle}
          >
            등급 변경
          </BasicButton>
          <BasicButton
            variant="outlined"
            onClick={() => setIsSuspensionDialogOpen(true)}
            disabled={!hasSelection}
            sx={buttonStyle}
          >
            활동 정지
          </BasicButton>
          <BasicButton
            variant="outlined"
            onClick={() => setIsForceWithdrawDialogOpen(true)}
            disabled={!hasSelection}
            sx={buttonStyle}
          >
            강제 탈퇴
          </BasicButton>
        </Stack>
      );
    }

    if (subTab === 'suspended') {
      return (
        <Stack direction="row" spacing={1} sx={{ mb: { xs: 2.5, md: 2 } }}>
          <BasicButton
            variant="outlined"
            onClick={() => setIsReactivationDialogOpen(true)}
            disabled={!hasSelection}
            sx={buttonStyle}
          >
            활동 정지 해제
          </BasicButton>
          <BasicButton
            variant="outlined"
            onClick={() => setIsForceWithdrawDialogOpen(true)}
            disabled={!hasSelection}
            sx={buttonStyle}
          >
            강제 탈퇴
          </BasicButton>
        </Stack>
      );
    }

    // withdrawn
    return (
      <BasicButton
        variant="outlined"
        onClick={() => setIsRejoinableChangeDialogOpen(true)}
        disabled={!hasSelection}
        sx={{ ...buttonStyle, mb: { xs: 2.5, md: 2 } }}
      >
        상태 변경
      </BasicButton>
    );
  };

  return (
    <>
      {/* 검색 */}
      <Stack spacing={'8px'} sx={{ mb: '30px' }}>
        <Typography fontSize={14} color={theme.palette.black[7]}>
          회원 검색
        </Typography>
        <Box
          sx={{
            p: 1.5,
            width: '100%',
            border: `1px solid ${theme.palette.grey.line}`,
            borderRadius: 0.5,
          }}
        >
          <Autocomplete
            options={[]}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="닉네임 또는 이메일 검색"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search
                        sx={{
                          width: { xs: 20, lg: 16 },
                          height: { xs: 20, lg: 16 },
                          color: theme.palette.black[1],
                        }}
                      />
                    </InputAdornment>
                  ),
                  onKeyPress: handleSearchKeyDown,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '16px', lg: '14px' },
                    padding: '0 !important',
                  },
                  '& .MuiInputBase-root': {
                    paddingRight: '0 !important',
                  },
                }}
              />
            )}
            value={searchTerm}
            inputValue={searchTerm}
            onSubmit={handleSearchClick}
            onInputChange={(_, newInputValue) => {
              setSearchTerm(newInputValue);
            }}
          />
        </Box>
      </Stack>

      {/* 액션 버튼 */}
      {renderActionButtons()}

      {/* 회원 목록 테이블 */}
      <ProgressBar isLoading={loading}>
        {error ? (
          <ErrorState />
        ) : !loading && users.length === 0 ? (
          <NoData message="결과 없음" />
        ) : (
          <Stack spacing={7.5}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                    <TableCell
                      padding="checkbox"
                      sx={{ borderRight: `1px solid ${theme.palette.grey.line}` }}
                    >
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllOnPageSelected}
                        onChange={handleSelectAllClick}
                        size="small"
                        sx={{
                          color: theme.palette.grey.line,
                          '&.Mui-checked': { color: '#f16438' },
                          '&.MuiCheckbox-indeterminate': { color: '#f16438' },
                        }}
                      />
                    </TableCell>
                    {headCells.map((headCell, index) => (
                      <TableCell
                        key={headCell.id}
                        align="center"
                        sortDirection={sortField === headCell.id ? sortOrder : false}
                        sx={{
                          whiteSpace: 'nowrap',
                          borderRight:
                            index === headCells.length - 1
                              ? 'none'
                              : `1px solid ${theme.palette.grey.line}`,
                        }}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={true}
                            direction={sortField === headCell.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(headCell.id)}
                            IconComponent={ArrowDropDownIcon}
                            sx={{
                              '&.Mui-active .MuiTableSortLabel-icon': {
                                color: theme.palette.black[3],
                              },
                            }}
                          >
                            <Typography fontSize={14} color={theme.palette.black[4]}>
                              {headCell.label}
                            </Typography>
                          </TableSortLabel>
                        ) : (
                          <Typography fontSize={14} color={theme.palette.black[4]}>
                            {headCell.label}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => {
                    const isItemSelected = selectedUserIds.indexOf(user.id) !== -1;
                    return (
                      <TableRow
                        key={user.id}
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        selected={isItemSelected}
                      >
                        <TableCell
                          padding="checkbox"
                          sx={{ borderRight: `1px solid ${theme.palette.grey.line}` }}
                        >
                          <Checkbox
                            checked={isItemSelected}
                            onClick={(event) => handleSelectClick(event, user.id)}
                            size="small"
                            sx={{
                              color: theme.palette.grey.line,
                              '&.Mui-checked': { color: '#f16438' },
                            }}
                          />
                        </TableCell>
                        {renderUserCells(user, subTab)}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={paginationInfo.totalPages}
              page={currentPage}
              onChange={handleChangePage}
            />
          </Stack>
        )}
      </ProgressBar>

      {/* 다이얼로그 */}
      <UserSuspensionDialog
        open={isSuspensionDialogOpen}
        onClose={() => setIsSuspensionDialogOpen(false)}
        onConfirm={handleSuspend}
      />
      <ActionDialog
        open={isReactivationDialogOpen}
        onClose={() => setIsReactivationDialogOpen(false)}
        onConfirm={handleReactivate}
        confirmText="변경"
      >
        <Typography sx={{ textAlign: 'center' }}>
          선택된 회원을 활동 멤버로 변경하시겠습니까?
        </Typography>
      </ActionDialog>
      <GradeChangeDialog
        open={isGradeChangeDialogOpen}
        onClose={() => setIsGradeChangeDialogOpen(false)}
        onConfirm={handleGradeChange}
        currentGrade={
          selectedUserIds.length === 1
            ? users.find((u) => u.id === selectedUserIds[0])?.grade
            : undefined
        }
      />
      <ForceWithdrawDialog
        open={isForceWithdrawDialogOpen}
        onClose={() => setIsForceWithdrawDialogOpen(false)}
        onConfirm={handleForceWithdraw}
      />
      <RejoinableChangeDialog
        open={isRejoinableChangeDialogOpen}
        onClose={() => setIsRejoinableChangeDialogOpen(false)}
        onConfirm={handleRejoinableChange}
      />
    </>
  );
};

export default UserManagement;
